// Request-access modal — opened by the pricing tier cards with a preset tier.
// Dark glass + gradient border; accessible (focus trap, focus restore, Esc,
// backdrop click, aria-modal); respects reduced-motion. Submits via the shared
// waitlist hook, which navigates to /joined on success.
import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CRWIcon } from './icons';
import { MetalBtn } from './primitives';
import { useWaitlistSubmit, type WaitlistTier } from './use-waitlist-submit';

const TIER_COPY: Record<WaitlistTier, { label: string; price: string }> = {
  monthly: { label: 'Monthly', price: '$29/mo · cancel anytime' },
  annual: { label: 'Annual', price: '$249/yr · 2 months free' },
  founding: { label: 'Founding Edition', price: '$199 once · $349 after launch' },
  general: { label: 'Early access', price: 'Launching soon · invite-only' },
};

export function RequestAccessModal({ tier, open, onClose }: { tier: WaitlistTier; open: boolean; onClose: () => void }) {
  const [email, setEmail] = useState('');
  const [trap, setTrap] = useState('');
  const { state, submit, clearError } = useWaitlistSubmit(tier, `modal:${tier}`);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const triggerRef = useRef<Element | null>(null);
  const titleId = useId();
  const reduced = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // open/close lifecycle: remember the trigger, lock scroll, focus the input,
  // restore focus on close.
  useEffect(() => {
    if (!open) return;
    triggerRef.current = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const t = window.setTimeout(() => inputRef.current?.focus(), 20);
    return () => {
      window.clearTimeout(t);
      document.body.style.overflow = prevOverflow;
      (triggerRef.current as HTMLElement | null)?.focus?.();
    };
  }, [open]);

  // reset the field whenever a fresh open happens
  useEffect(() => { if (open) { setEmail(''); setTrap(''); clearError(); } /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [open, tier]);

  // Esc to close + a simple focus trap within the dialog
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); return; }
      if (e.key !== 'Tab') return;
      const root = dialogRef.current;
      if (!root) return;
      const f = Array.from(root.querySelectorAll<HTMLElement>('a[href],button:not([disabled]),input:not([disabled]):not([tabindex="-1"]),[tabindex]:not([tabindex="-1"])'));
      if (!f.length) { e.preventDefault(); return; }
      const first = f[0], last = f[f.length - 1];
      const active = document.activeElement;
      // pull focus back if it ever lands outside the dialog (e.g. a Tab fired
      // before the open-autofocus landed) — the trap can never leak.
      if (!root.contains(active)) { e.preventDefault(); first.focus(); return; }
      if (e.shiftKey && active === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  const t = TIER_COPY[tier];

  return createPortal(
    <div
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 100, display: 'grid', placeItems: 'center', padding: 20,
        background: 'rgba(4,5,6,0.72)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
        animation: reduced ? undefined : 'crwPageIn 200ms var(--ease-out) both',
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        style={{
          position: 'relative', width: '100%', maxWidth: 480, borderRadius: 18, padding: 1,
          background: 'linear-gradient(170deg, rgba(74,227,255,0.55), rgba(255,255,255,0.08) 38%, rgba(139,124,255,0.45) 88%)',
          boxShadow: '0 40px 110px rgba(0,0,0,0.7), 0 0 80px rgba(74,227,255,0.12)',
        }}
      >
        <div style={{ position: 'relative', borderRadius: 17, background: 'rgba(8,8,10,0.97)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', padding: '26px 26px 24px', overflow: 'hidden' }}>
          <span aria-hidden="true" style={{ position: 'absolute', top: 0, left: '10%', right: '10%', height: 1, background: 'linear-gradient(90deg, transparent, var(--signal-400), transparent)' }}></span>

          <button
            onClick={onClose}
            aria-label="Close"
            className="crw-modal-x"
            style={{ position: 'absolute', top: 14, right: 14 }}
          >
            <CRWIcon d="x" size={15} />
          </button>

          <span className="crw-eyebrow"><i></i>Request early access</span>
          <h2 id={titleId} style={{ margin: '12px 0 4px', fontFamily: 'var(--font-display)', fontSize: 26, fontWeight: 700, letterSpacing: 'var(--tracking-display)', color: 'var(--fg-1)' }}>
            {t.label}
          </h2>
          <p style={{ margin: 0, fontFamily: 'var(--font-mono)', fontSize: 12, letterSpacing: '0.04em', color: 'var(--signal-300)' }}>{t.price}</p>

          <p style={{ margin: '16px 0 14px', fontSize: 13.5, lineHeight: 1.6, color: 'var(--fg-2)' }}>
            We’ll email your invite the moment we open up — no card required.
          </p>

          <input
            ref={inputRef}
            value={email}
            onChange={(e) => { setEmail(e.target.value); clearError(); }}
            onKeyDown={(e) => { if (e.key === 'Enter') void submit(email, trap); }}
            type="email"
            placeholder="you@studio.com"
            aria-label="Email address"
            aria-invalid={state === 'error'}
            className="crw-input"
            style={{ width: '100%', boxSizing: 'border-box', fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--fg-1)', background: 'rgba(255,255,255,0.04)', border: `1px solid ${state === 'error' ? 'rgba(248,113,113,0.6)' : 'rgba(255,255,255,0.14)'}`, borderRadius: 10, padding: '13px 14px', boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.4)' }}
          />
          {/* honeypot */}
          <input value={trap} onChange={(e) => setTrap(e.target.value)} name="company" tabIndex={-1} autoComplete="off" aria-hidden="true" style={{ position: 'absolute', left: -9999, width: 1, height: 1, opacity: 0, pointerEvents: 'none' }} />

          <div style={{ marginTop: 14 }}>
            <MetalBtn onClick={() => void submit(email, trap)} busy={state === 'sending'} style={{ width: '100%' }}>
              {state === 'sending' ? 'Requesting…' : 'Request early access'}
            </MetalBtn>
          </div>
          {state === 'error' && (
            <p role="alert" style={{ margin: '12px 0 0', textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '0.04em', color: '#f87171' }}>
              That didn’t go through — check the email and try again.
            </p>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
